import { Component, OnInit, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ReseauScreenComponent } from './screens/reseau-screen.component';
import { MonSiteScreenComponent } from './screens/mon-site-screen.component';
import { MessageScreenComponent } from './screens/message-screen.component';
import { ActusScreenComponent } from './screens/actus-screen.component';
import { SiteDetailScreenComponent } from './screens/site-detail-screen.component';
import { DashboardScreenComponent } from './screens/dashboard-screen.component';
import { CommPatientScreenComponent } from './screens/comm-patient-screen.component';
import { CanalScreenComponent } from './screens/canal-screen.component';
import { ModeDegradeScreenComponent } from './screens/mode-degrade-screen.component';
import { RenfortScreenComponent, RenfortSentData } from './screens/renfort-screen.component';
import { AvisPatientScreenComponent } from './screens/avis-patient-screen.component';
import { SatisfactionScreenComponent } from './screens/satisfaction-screen.component';
import { Site, CanalCreatedData } from './screens/screens.types';

export type AppTab = 'reseau' | 'mon-site' | 'message' | 'actus';

@Component({
  selector: 'app-mobile-app',
  standalone: true,
  imports: [ReseauScreenComponent, MonSiteScreenComponent, MessageScreenComponent, ActusScreenComponent, SiteDetailScreenComponent, DashboardScreenComponent, CommPatientScreenComponent, CanalScreenComponent, ModeDegradeScreenComponent, RenfortScreenComponent, AvisPatientScreenComponent, SatisfactionScreenComponent, TranslatePipe],
  templateUrl: './mobile-app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mobile-app.component.scss'],
})
export class MobileAppComponent implements OnInit {
  readonly activeTab = signal<AppTab>('reseau');
  readonly showNotifications = signal(false);
  readonly isScrolled = signal(false);
  readonly scrollY = signal(0);
  readonly notificationCount = signal(3);
  readonly currentTime = signal(this.formatTime());
  readonly selectedSite = signal<Site | null>(null);
  readonly detailExiting = signal(false);
  readonly reseauView = signal<'dashboard' | 'list'>('dashboard');
  readonly commPatientOpen = signal(false);
  readonly commPatientExiting = signal(false);
  readonly canalOpen = signal(false);
  readonly canalExiting = signal(false);
  readonly directMessageNom = signal<string | null>(null);
  readonly modeDegradeOpen = signal(false);
  readonly modeDegradeExiting = signal(false);
  readonly renfortOpen = signal(false);
  readonly renfortExiting = signal(false);
  readonly renfortSiteName = signal('');
  readonly renfortSiteStatut = signal('');
  readonly renfortTensionMinutes = signal<number | null>(null);
readonly pushNotif = signal<{ title: string; body: string } | null>(null);
  private pushTimer: any;
  readonly canalFilterOpen = signal(false);
  readonly canalFilterRoles = signal<string[]>([]);
  readonly canalFilterSites = signal<string[]>([]);
  readonly actusFilterOpen = signal(false);
  readonly actusFilterSites = signal<string[]>([]);
  readonly notifAideOpen = signal(false);
  readonly notifAideSites = signal<string[]>([]);
  readonly notifAideSent = signal(false);
  readonly avisPatientOpen = signal(false);
  readonly avisPatientExiting = signal(false);
  readonly satisfactionOpen = signal(false);
  readonly satisfactionExiting = signal(false);
  readonly pendingCanal = signal<CanalCreatedData | null>(null);
  readonly sheetDragDelta = signal(0);
  readonly sheetIsDragging = signal(false);
  private sheetStartY = 0;

  readonly canalAllRoles = ['Biologiste', 'Responsable accueil', 'Infirmière', 'Directeur opérationnel', 'RAQ', 'Technicien de labo'];
  readonly canalAllSites = ['Nantes Centre', 'Carquefou', 'Saint-Herblain', 'Réseau', 'Rezé', 'Orvault'];
  readonly actusAllSites = ['Nantes Centre', 'Carquefou', 'Saint-Herblain', 'Rezé', 'Orvault', 'Réseau'];
  readonly notifAideAllSites = ['Rezé', 'Orvault', 'Carquefou', 'Saint-Herblain', 'Vertou'];

  readonly canalHasFilter = computed(() => this.canalFilterRoles().length > 0 || this.canalFilterSites().length > 0);

  toggleCanalRole(role: string) {
    const c = this.canalFilterRoles();
    this.canalFilterRoles.set(c.includes(role) ? c.filter(r => r !== role) : [...c, role]);
  }

  toggleCanalSite(site: string) {
    const c = this.canalFilterSites();
    this.canalFilterSites.set(c.includes(site) ? c.filter(s => s !== site) : [...c, site]);
  }

  resetCanalFilters() {
    this.canalFilterRoles.set([]);
    this.canalFilterSites.set([]);
  }

  openActusFilter() {
    this.sheetDragDelta.set(600);
    this.actusFilterOpen.set(true);
    requestAnimationFrame(() => requestAnimationFrame(() => this.sheetDragDelta.set(0)));
  }

  closeActusFilter() {
    this.sheetDragDelta.set(600);
    setTimeout(() => {
      this.actusFilterOpen.set(false);
      this.sheetDragDelta.set(0);
    }, 280);
  }

  toggleActusSite(site: string) {
    const c = this.actusFilterSites();
    this.actusFilterSites.set(c.includes(site) ? c.filter(s => s !== site) : [...c, site]);
  }

  resetActusFilters() {
    this.actusFilterSites.set([]);
  }

  openNotifAide() {
    this.notifAideSites.set([]);
    this.notifAideSent.set(false);
    this.sheetDragDelta.set(600);
    this.notifAideOpen.set(true);
    requestAnimationFrame(() => requestAnimationFrame(() => this.sheetDragDelta.set(0)));
  }

  closeNotifAide() {
    this.sheetDragDelta.set(600);
    setTimeout(() => {
      this.notifAideOpen.set(false);
      this.sheetDragDelta.set(0);
    }, 280);
  }

  toggleNotifAideSite(site: string) {
    const c = this.notifAideSites();
    this.notifAideSites.set(c.includes(site) ? c.filter(s => s !== site) : [...c, site]);
  }

  sendNotifAide() {
    this.notifAideSent.set(true);
    setTimeout(() => this.closeNotifAide(), 1400);
  }

  openCanalFilter() {
    this.sheetDragDelta.set(600);
    this.canalFilterOpen.set(true);
    requestAnimationFrame(() => requestAnimationFrame(() => this.sheetDragDelta.set(0)));
  }

  closeCanalFilter() {
    this.sheetDragDelta.set(600);
    setTimeout(() => {
      this.canalFilterOpen.set(false);
      this.sheetDragDelta.set(0);
    }, 280);
  }

  onSheetPointerDown(e: PointerEvent) {
    this.sheetIsDragging.set(true);
    this.sheetStartY = e.clientY;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  onSheetPointerMove(e: PointerEvent) {
    if (!this.sheetIsDragging()) return;
    this.sheetDragDelta.set(Math.max(0, e.clientY - this.sheetStartY));
  }

  onSheetPointerUp() {
    if (!this.sheetIsDragging()) return;
    this.sheetIsDragging.set(false);
    if (this.sheetDragDelta() > 100) {
      if (this.canalFilterOpen())   this.closeCanalFilter();
      else if (this.actusFilterOpen()) this.closeActusFilter();
      else if (this.notifAideOpen())   this.closeNotifAide();
    } else {
      this.sheetDragDelta.set(0);
    }
  }
  readonly isDetailOpen = computed(() => !!this.selectedSite() && !this.detailExiting());

  readonly monSite: Site = {
    id: 'nantes-centre', nom: 'NANTES CENTRE', statut: 'critique',
    tensionLabel: 'TENSION CRITIQUE', descInfo: 'Dégradation continue',
    activite: 'très élevée', activiteLevel: 'critique', presenceEstimee: 38,
    alertes: [
      { text: 'Alerte envoyée', type: 'default' },
      { text: 'Mode dégradé non activé', type: 'warn' },
    ],
    showActionBtns: false,
    timeline: ['vert', 'vert', 'orange', 'orange', 'rouge', 'rouge'],
    summary: '"Tension continue depuis 45 min"',
  };

  readonly activeSiteForOverlay = computed(() => this.selectedSite() ?? this.monSite);

  private formatTime(): string {
    return new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }

  ngOnInit() {
    setInterval(() => this.currentTime.set(this.formatTime()), 10000);
  }

  onListScroll(e: Event) {
    const top = (e.target as HTMLElement).scrollTop;
    this.scrollY.set(top);
    this.isScrolled.set(top > 6);
  }

  setTab(tab: AppTab) {
    this.isScrolled.set(false);
    this.scrollY.set(0);
    this.activeTab.set(tab);
    this.showNotifications.set(false);
    this.selectedSite.set(null);
    this.detailExiting.set(false);
    this.reseauView.set('dashboard');
    this.renfortOpen.set(false);
    this.renfortExiting.set(false);
    this.modeDegradeOpen.set(false);
    this.modeDegradeExiting.set(false);
    this.commPatientOpen.set(false);
    this.commPatientExiting.set(false);
    this.canalOpen.set(false);
    this.canalExiting.set(false);
    this.avisPatientOpen.set(false);
    this.avisPatientExiting.set(false);
    this.satisfactionOpen.set(false);
    this.satisfactionExiting.set(false);
  }

  showList() { this.reseauView.set('list'); }
  showDashboard() { this.reseauView.set('dashboard'); }

  openSite(site: Site) {
    if (site.id === 'nantes-centre') {
      this.setTab('mon-site');
      return;
    }
    this.detailExiting.set(false);
    this.selectedSite.set(site);
  }

  closeSite() {
    this.detailExiting.set(true);
    setTimeout(() => {
      this.selectedSite.set(null);
      this.detailExiting.set(false);
    }, 320);
  }

  openCommPatient() {
    this.commPatientExiting.set(false);
    this.commPatientOpen.set(true);
  }

  closeCommPatient() {
    this.commPatientExiting.set(true);
    setTimeout(() => {
      this.commPatientOpen.set(false);
      this.commPatientExiting.set(false);
    }, 320);
  }

  openModeDegrade() {
    this.modeDegradeExiting.set(false);
    this.modeDegradeOpen.set(true);
  }

  closeModeDegrade() {
    this.modeDegradeExiting.set(true);
    setTimeout(() => {
      this.modeDegradeOpen.set(false);
      this.modeDegradeExiting.set(false);
    }, 320);
  }

  openRenfort(siteName: string, siteStatut: string, forcedMinutes?: number) {
    this.renfortSiteName.set(siteName);
    this.renfortSiteStatut.set(siteStatut);
    if (forcedMinutes !== undefined) {
      this.renfortTensionMinutes.set(forcedMinutes);
    } else {
      const tl = this.selectedSite()?.timeline ?? [];
      const firstIdx = tl.findIndex(c => c !== 'vert');
      this.renfortTensionMinutes.set(firstIdx !== -1 ? (5 - firstIdx) * 15 : null);
    }
    this.renfortExiting.set(false);
    this.renfortOpen.set(true);
  }

  closeRenfort() {
    this.renfortExiting.set(true);
    setTimeout(() => {
      this.renfortOpen.set(false);
      this.renfortExiting.set(false);
    }, 320);
  }

  onRenfortSent(data: RenfortSentData) {
    const notif = {
      title: `🆘 Renfort demandé — ${data.siteName}`,
      body: `Message envoyé à ${data.siteCount} site${data.siteCount > 1 ? 's' : ''}. Les équipes disponibles ont été alertées.`,
    };
    this.notifications = [
      { id: Date.now(), severity: 'warning', title: notif.title, body: notif.body, time: 'À l\'instant' },
      ...this.notifications,
    ];
    this.notificationCount.update(n => n + 1);
    clearTimeout(this.pushTimer);
    this.pushNotif.set({ title: notif.title, body: notif.body });
    this.pushTimer = setTimeout(() => this.pushNotif.set(null), 9000);
    this.closeRenfort();
  }

  onModeDegradeActivated() {
    const site = this.activeSiteForOverlay();
    if (site) {
      const notif = {
        title: `Mode dégradé activé — ${site.nom}`,
        body: 'Le protocole a été activé afin de fluidifier temporairement la prise en charge.',
      };
      this.notifications = [
        { id: Date.now(), severity: 'warning', title: notif.title, body: notif.body, time: 'À l\'instant' },
        ...this.notifications,
      ];
      this.notificationCount.update(n => n + 1);
      clearTimeout(this.pushTimer);
      this.pushNotif.set({ title: `🟠 ${notif.title}`, body: notif.body });
      this.pushTimer = setTimeout(() => this.pushNotif.set(null), 9000);
    }
    this.closeModeDegrade();
  }

  onPushNotifClick() {
    this.pushNotif.set(null);
    clearTimeout(this.pushTimer);
    this.toggleNotifications();
  }

  openCanal() {
    this.canalExiting.set(false);
    this.canalOpen.set(true);
  }

  severityLabel(s: string): string {
    return ({ critique: 'Critique', warning: 'Attention', info: 'Info' })[s] ?? s;
  }

  openAvisPatientScreen() {
    this.avisPatientExiting.set(false);
    this.avisPatientOpen.set(true);
  }

  closeAvisPatient() {
    this.avisPatientExiting.set(true);
    setTimeout(() => {
      this.avisPatientOpen.set(false);
      this.avisPatientExiting.set(false);
    }, 320);
  }

  openSatisfactionScreen() {
    this.satisfactionExiting.set(false);
    this.satisfactionOpen.set(true);
  }

  closeSatisfaction() {
    this.satisfactionExiting.set(true);
    setTimeout(() => {
      this.satisfactionOpen.set(false);
      this.satisfactionExiting.set(false);
    }, 320);
  }

  openCanalWithCollegue(nom: string) {
    this.directMessageNom.set(nom);
    this.setTab('message');
    setTimeout(() => this.directMessageNom.set(null), 100);
  }

  closeCanal() {
    this.canalExiting.set(true);
    setTimeout(() => {
      this.canalOpen.set(false);
      this.canalExiting.set(false);
    }, 320);
  }

  onCanalCreated(data: CanalCreatedData) {
    this.pendingCanal.set(data);
    this.canalExiting.set(true);
    setTimeout(() => {
      this.canalOpen.set(false);
      this.canalExiting.set(false);
      this.selectedSite.set(null);
      this.activeTab.set('message');
      setTimeout(() => this.pendingCanal.set(null), 100);
    }, 320);
  }

  toggleNotifications() {
    this.showNotifications.set(!this.showNotifications());
    if (this.showNotifications()) this.notificationCount.set(0);
  }

  markAllRead() {
    this.notificationCount.set(0);
  }

  notifications: { id: number; severity: 'critique' | 'warning' | 'info'; title: string; body: string; time: string }[] = [
    { id: 1, severity: 'critique', title: 'Nantes Centre — Critique',  body: 'Dégradation continue depuis 45 min. Renfort recommandé.',  time: 'Il y a 2 min'  },
    { id: 2, severity: 'warning',  title: 'Saint-Herblain — Tension',  body: 'Flux en augmentation depuis 30 min. Accumulation de personne sur site.',       time: 'Il y a 11 min' },
    { id: 3, severity: 'info',     title: 'Paul R. en route',          body: 'Paul R. (Préleveur) arrive sur Nantes Centre dans ~10 min. Renfort confirmé.',  time: 'Il y a 8 min'  },
    { id: 4, severity: 'info',     title: 'Thomas M. disponible',      body: 'Thomas M. (Biologiste, Orvault) peut absorber vos résultats à distance.',        time: 'Il y a 14 min' },
    { id: 5, severity: 'info',     title: 'Marie L. disponible',       body: 'Marie L. (Préleveur, Rezé) signale sa disponibilité pour renfort immédiat.',     time: 'Il y a 18 min' },
  ];
}